import React, { useEffect, useMemo } from "react";
import { useCart } from "./UseCart";
import { remove_from_cart, update_cart } from "../../services/shop/cart";
import LoadingScreen from "../common/LoadingScreen";
import { Link, useOutletContext } from "react-router-dom";
import { formatLBP } from "../../services/utils/currency";

const Cart = () => {
  const { cart, totalItems, discountPercent, loading, setCart, setTotalItems, setTotalPrice } = useCart();

  const { setCartCount } = useOutletContext();

  const { subtotal, itemsCount } = useMemo(() => {
    const sub = cart.reduce((sum, it) => sum + Number(it.price) * Number(it.quantity), 0);
    const count = cart.reduce((sum, it) => sum + Number(it.quantity), 0);
    return { subtotal: sub, itemsCount: count };
  }, [cart]);

  const autoDiscount = Math.round((subtotal * (Number(discountPercent) || 0)) / 100);

  useEffect(() => {
    setTotalItems(itemsCount);
    setTotalPrice(subtotal);
  }, [itemsCount, subtotal, setTotalItems, setTotalPrice]);

  if (loading) return <LoadingScreen />;

  const updateQty = (item_id, delta) => {
    const current = cart.find((it) => it.item_id === item_id);
    if (!current) return;
    const nextQty = Math.max(1, current.quantity + delta);

    setCart(curr =>
      curr.map((it) => (
        it.item_id === item_id 
        ? { ...it, quantity: nextQty, subtotal: nextQty * it.price } 
        : it
      ))
    );

    update_cart(item_id, nextQty)
      .then(res => {
        const items = Object.entries(res.cart || {}).map(([key, v]) => ({
          id: key,
          item_id: v.item_id ?? v.id ?? null,
          product_id: v.product_id,
          name: v.name,
          quantity: v.quantity,
          price: v.price,
          slug: v.slug,
          image: v.image || "/placeholder.jpg",
          subtotal: v.price * v.quantity,
          preview: v.preview,
          is_custom: v.is_custom,
        }));
        setCart(items);
        setTotalItems(res.total_items);
        setTotalPrice(res.total_price);
      })
      .catch(err => {
        console.error("Update failed", err);
      });
  };


  const handleRemove = (e, item_id) => {
    e.preventDefault();
    remove_from_cart(item_id).then((res) => {
      console.log("Cart after removal:", res.cart);
      const items = Object.entries(res.cart).map(([key, v]) => ({
        id: key,
        item_id: v.item_id ?? v.id ?? null,
        product_id: v.product_id,
        name: v.name,
        quantity: v.quantity,
        price: v.price,
        slug: v.slug,
        image: v.image || "/placeholder.jpg",
        subtotal: v.price * v.quantity,
        preview: v.preview,
        is_custom: v.is_custom,
      }));
      setCart(items);
      setTotalItems(res.total_items);
      setTotalPrice(res.total_price);
      setCartCount(res.total_items);
    });
  };

  if (totalItems === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{height: "100vh"}}>
        <div className="text-center">
          <h4 className="text-primary">Bag is empty</h4>
          <a className="btn btn-primary rounded-0" href="/">
            Start Shopping
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row">
        <div className="col-lg-8">
          <div className="m-4">
            <h4 className="card-title mb-4 color-primary">Your shopping bag</h4>
            {cart.map((item) => (
              <div
                className="row gy-3 mb-5 mb-md-4 d-flex align-items-center border-bottom pb-2 border-light-beige"
                key={item.id}
              >
                <div className="col-lg-5">
                  <div className="me-lg-5">
                    <div className="d-flex align-items-center">
                      <div className="me-3 d-flex justify-content-center align-items-center">
                        {item.preview ? 
                          <img
                            src={item.preview}
                            className="img-thumbnail-cart"
                            alt={item.name}
                            loading="lazy"
                          />
                          :
                          <img
                            src={item.image}
                            className="img-thumbnail-cart"
                            alt={item.name}
                            loading="lazy"
                          />
                        }
                      </div>

                      <div className="">
                        {item.name}
                        <p className="text-muted">{formatLBP(item.price)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-6 d-flex flex-column flex-xl-row text-nowrap align-items-start align-items-xl-center gap-2">
                  <div className="input-group flex-nowrap border border-brown w-100 w-md-50 small-h-cart me-xl-4">
                    <button
                      className="btn color-primary fs-6 border-0 px-2"
                      type="button"
                      onClick={() => updateQty(item.item_id, -1)}
                      disabled={item.quantity <= 1}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      className="form-control text-center p-0 border-0 bg-light-beige color-primary fs-6 fw-bold"
                      value={item.quantity}
                      readOnly
                    />
                    <button
                      className="btn color-primary fs-6 border-0 px-2"
                      type="button"
                      onClick={() => updateQty(item.item_id, +1)}
                    >
                      +
                    </button>
                  </div>
                  <div className="">
                    <text className="h6 color-primary">{formatLBP(item.price*item.quantity)}</text>
                  </div>
                </div>
                <div className="col-lg-3 col-6 d-flex justify-content-end mb-0">
                  <div className="float-md-end">
                    {item.is_custom && (
                        <Link
                          className="remove-cart text-light-brown me-4"
                          to={`/products/${item.slug}?item=${item.item_id}`}
                        >
                          Edit
                        </Link>
                      )}
                    <button
                      type="button"
                      className="remove-cart text-light-brown"
                      onClick={(e) => handleRemove(e, item.item_id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="m-4">
            <div className="card rounded-0 bg-light-beige shadow-0 border-light-beige border">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <p className="mb-2">Subtotal</p>
                  <p className="mb-2">{formatLBP(subtotal)}</p>
                </div>
                <div className="d-flex justify-content-between">
                  <p className="mb-2">Discount{discountPercent ? ` (${discountPercent}%)` : ""}</p>
                  <p className="mb-2 text-primary">{autoDiscount ? `- ${formatLBP(autoDiscount)}` : formatLBP(0)}</p>
                </div>
                <div className="d-flex justify-content-between">
                  <p className="mb-2">Shipping</p>
                  <p className="mb-2 text-primary small d-flex align-items-end">Calculated on checkout</p>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <p className="mb-2">Total</p>
                  <p className="mb-2 fw-bold">{formatLBP(subtotal - autoDiscount)}</p>
                </div>

                <div className="mt-3">
                  <a href="/checkout" className="btn btn-primary w-100 shadow-0 mb-2 rounded-0">
                    Proceed To Checkout
                  </a>
                  <a href="/" className="btn btn-light w-100 border mt-2 rounded-0 bg-light-beige-input">
                    Continue Shopping
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
